import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Add as AddIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

const ExaminationManage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [exam, setExam] = useState<any>(null);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  
  const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
  const [selectedExamClassId, setSelectedExamClassId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [maxMarks, setMaxMarks] = useState(100);
  const [passMarks, setPassMarks] = useState(40);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [examRes, classRes, subjectRes] = await Promise.all([
        axios.get(`/exams/${id}`),
        axios.get('/system-settings/masters/class'),
        axios.get('/system-settings/masters/subject'),
      ]);
      setExam(examRes.data);
      setClasses(classRes.data);
      setSubjects(subjectRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignClass = async () => {
    try {
      await axios.post(`/exams/${id}/classes`, { classId: selectedClass });
      setOpenClassDialog(false);
      fetchData(); // Refresh
    } catch (error) {
      console.error('Failed to assign class:', error);
    }
  };

  const handleConfigSubject = async () => {
    try {
      await axios.post(`/exams/classes/${selectedExamClassId}/subjects`, {
        subjectId: selectedSubject,
        maxMarks: Number(maxMarks),
        passMarks: Number(passMarks),
      });
      setOpenSubjectDialog(false);
      fetchData(); // Refresh
    } catch (error) {
      console.error('Failed to config subject:', error);
    }
  };

  if (loading || !exam) return <MainLayout><Typography>Loading...</Typography></MainLayout>;

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4">{exam.name}</Typography>
          <Typography color="textSecondary">
            {new Date(exam.startDate).toLocaleDateString()} - {new Date(exam.endDate).toLocaleDateString()}
          </Typography>
        </Box>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenClassDialog(true)}>
            Assign Class
          </Button>
        </Box>

        {exam.classes?.map((examClass: any) => (
          <Accordion key={examClass.id} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{examClass.class.name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Button 
                  size="small" 
                  startIcon={<AddIcon />} 
                  onClick={() => {
                    setSelectedExamClassId(examClass.id);
                    setOpenSubjectDialog(true);
                  }}
                >
                  Add Subject
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Subject</TableCell>
                      <TableCell>Max Marks</TableCell>
                      <TableCell>Pass Marks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {examClass.subjects?.map((sub: any) => (
                      <TableRow key={sub.id}>
                        <TableCell>{sub.subject.name}</TableCell>
                        <TableCell>{sub.maxMarks}</TableCell>
                        <TableCell>{sub.passMarks}</TableCell>
                      </TableRow>
                    ))}
                    {(!examClass.subjects || examClass.subjects.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No subjects configured</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Assign Class Dialog */}
        <Dialog open={openClassDialog} onClose={() => setOpenClassDialog(false)}>
          <DialogTitle>Assign Class</DialogTitle>
          <DialogContent sx={{ minWidth: 300, pt: 2 }}>
            <TextField
              select
              label="Class"
              fullWidth
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              sx={{ mt: 1 }}
            >
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenClassDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignClass} variant="contained">Assign</Button>
          </DialogActions>
        </Dialog>

        {/* Config Subject Dialog */}
        <Dialog open={openSubjectDialog} onClose={() => setOpenSubjectDialog(false)}>
          <DialogTitle>Configure Subject</DialogTitle>
          <DialogContent sx={{ minWidth: 300, pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Subject"
                  fullWidth
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  sx={{ mt: 1 }}
                >
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Marks"
                  type="number"
                  fullWidth
                  value={maxMarks}
                  onChange={(e) => setMaxMarks(Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Pass Marks"
                  type="number"
                  fullWidth
                  value={passMarks}
                  onChange={(e) => setPassMarks(Number(e.target.value))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSubjectDialog(false)}>Cancel</Button>
            <Button onClick={handleConfigSubject} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default ExaminationManage;
